import { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../api/api';
import { EQueryStringParam } from '../util/constants';
import GlobalContext from './GlobalContext';

export enum ELogoutUiState {
    default,
    queryStringParsed,
    ready,
    success,
    redirect,
    fatalError,
}

type TLogoutContext = {
    logoutUiState: ELogoutUiState;
    logout: () => Promise<void>;
    redirectUrl: string;
};

const LogoutContext = createContext<TLogoutContext | undefined>(undefined);

export const LogoutProvider: FC = (props) => {
    const { children } = props;
    const { csrfToken, getCsrfToken } = useContext(GlobalContext);
    const [logoutUiState, setLogoutUiState] = useState(ELogoutUiState.default);
    const [logoutId, setLogoutId] = useState('');
    const [redirectUrl, setRedirectUrl] = useState('');
    const { search: queryParams } = useLocation();

    // --------------
    // Setup
    // --------------
    const parseQueryString = useCallback(() => {
        const params = new URLSearchParams(queryParams);
        const logoutId = params.get(EQueryStringParam.logoutId);
        if (logoutId) {
            setLogoutId(logoutId);
            setLogoutUiState(ELogoutUiState.queryStringParsed);
        } else {
            console.error('can not find logoutId in query string');
            setLogoutUiState(ELogoutUiState.fatalError);
        }
    }, [queryParams]);

    const getInitialData = useCallback(async () => {
        try {
            await getCsrfToken();
            setLogoutUiState(ELogoutUiState.ready);
        } catch {
            console.error('Failed to get csrf token');
            setLogoutUiState(ELogoutUiState.fatalError);
        }
    }, [getCsrfToken]);

    useEffect(() => {
        switch (logoutUiState) {
            case ELogoutUiState.default:
                parseQueryString();
                break;
            case ELogoutUiState.queryStringParsed:
                getInitialData();
                break;
            default:
                break;
        }
        if (logoutUiState === ELogoutUiState.default) {
            parseQueryString();
        }
    }, [logoutUiState, parseQueryString, getInitialData]);

    // --------------
    // Api calls
    // --------------
    const logout = async () => {
        const payload = { logoutId };
        try {
            const response = await API.post.logout(payload, csrfToken);
            const { postLogoutRedirectUri } = response.data;
            if (postLogoutRedirectUri) {
                setRedirectUrl(postLogoutRedirectUri);
                setLogoutUiState(ELogoutUiState.redirect);
            } else {
                setLogoutUiState(ELogoutUiState.success);
            }
        } catch {
            console.error('Failed to logout user');
            setLogoutUiState(ELogoutUiState.fatalError);
        }
    };

    return (
        <LogoutContext.Provider
            value={{
                logoutUiState,
                logout,
                redirectUrl,
            }}
        >
            {children}
        </LogoutContext.Provider>
    );
};

const useLogoutContext = () => {
    const context = useContext(LogoutContext);
    if (context === undefined) {
        throw new Error('useLogoutContext must be used within a LogoutProvider');
    }
    return context;
};

export default useLogoutContext;
